import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../../app.store';

import type {
  CloseModalPayload,
  DiscloserScopeState,
  DiscloserState,
  OpenModalPayload,
} from './discloser.types';

const initialState: DiscloserState = {
  scopes: {},
};

const discloserSlice = createSlice({
  name: 'discloser',
  initialState,
  reducers: {
    // Initialize modal scope
    initModal: (state, action: PayloadAction<{ scope: string }>) => {
      const { scope } = action.payload;
      if (!state.scopes[scope]) {
        state.scopes[scope] = { open: false, type: 'VIEW', data: undefined };
      }
    },

    // Open modal for scope
    openModal: <TypeT, DataT>(
      state: DiscloserState,
      action: PayloadAction<OpenModalPayload<TypeT, DataT>>,
    ) => {
      const { scope, type, data } = action.payload;
      state.scopes[scope] = {
        open: true,
        type,
        data,
      } as DiscloserScopeState<TypeT, DataT>;
    },

    // Close modal for scope
    closeModal: (state, action: PayloadAction<CloseModalPayload>) => {
      const { scope } = action.payload;
      if (state.scopes[scope]) {
        state.scopes[scope] = { open: false } as DiscloserScopeState;
      }
    },

    // Clear specific modal scope
    clearModal: (state, action: PayloadAction<{ scope: string }>) => {
      const { scope } = action.payload;
      if (state.scopes[scope]) {
        state.scopes[scope] = { open: false, type: 'VIEW', data: undefined };
      }
    },

    // Clear all modal scopes
    clearAllModals: (state) => {
      state.scopes = {};
    },
  },
});

export const { initModal, openModal, closeModal, clearModal, clearAllModals } =
  discloserSlice.actions;

export const discloserReducer = discloserSlice.reducer;

// Select all modal scopes
export const selectDiscloser = (state: RootState) => state?.discloser || {};

// Base selector for modal scopes
const selectModalScopes = (state: RootState) => state?.discloser?.scopes || {};

// Memoized selector for modal scope by name
export const selectModalScope = createSelector(
  [selectModalScopes, (_state: RootState, scope: string) => scope],
  (scopes, scope) => scopes[scope] || { open: false },
);
